import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment from "moment";

/**
 * SubConversations Component
 * Handles display and interaction with subconversations in chat
 *
 * @param {Object} props
 * @param {Array} props.subConversations - Array of subconversation objects
 * @param {Function} props.onSubConversationSelect - Callback when subconversation is selected
 * @param {Function} props.onSubConversationMenuOpen - Callback for menu options
 * @param {string} props.selectedSubConversationId - Currently selected subconversation ID
 * @param {Function} props.onSelectionChange - Callback when selection changes
 */
const SubConversations = ({
  subConversations = [],
  onSubConversationSelect,
  onSubConversationMenuOpen,
  selectedSubConversationId,
  onSelectionChange,
}) => {
  // State for expand/collapse
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle subconversation selection
  const handleSubConversationSelect = (subConvId) => {
    if (selectedSubConversationId === subConvId) {
      // If already selected, deselect it
      onSelectionChange(null);
    } else {
      // Select the new subconversation
      onSelectionChange(subConvId);
    }
  };

  // Handle expand/collapse toggle
  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Sort sub-conversations: selected first, then by last message updatedAt or sub-conversation updatedAt
  const getSortedSubConversations = () => {
    if (!subConversations || subConversations.length === 0) {
      return [];
    }

    const sorted = [...subConversations].sort((a, b) => {
      // Selected sub-conversation always comes first
      if (a._id === selectedSubConversationId) return -1;
      if (b._id === selectedSubConversationId) return 1;

      // Get the most recent time for sorting (last message or sub-conversation update)
      const getLastUpdateTime = (subConv) => {
        // Priority 1: lastMessage updatedAt
        if (subConv?.lastMessage?.updatedAt) {
          return new Date(subConv.lastMessage.updatedAt).getTime();
        }
        // Priority 2: sub-conversation updatedAt
        if (subConv?.updatedAt) {
          return new Date(subConv.updatedAt).getTime();
        }
        // Priority 3: sub-conversation createdAt
        if (subConv?.createdAt) {
          return new Date(subConv.createdAt).getTime();
        }
        // Fallback: current time
        return new Date().getTime();
      };

      const aTime = getLastUpdateTime(a);
      const bTime = getLastUpdateTime(b);

      // Most recent first (higher timestamp = more recent)
      return bTime - aTime;
    });

    return sorted;
  };

  // Clear selection when subconversations change
  useEffect(() => {
    if (
      selectedSubConversationId &&
      !subConversations.find((sc) => sc._id === selectedSubConversationId)
    ) {
      onSelectionChange(null);
    }
  }, [subConversations, selectedSubConversationId, onSelectionChange]);

  // Don't render if no subconversations
  if (!subConversations || subConversations.length === 0) {
    return null;
  }

  // Shared card renderer to avoid duplication
  const renderCard = (subConv, { isCollapsed = false, stackIndex } = {}) => {
    const isSelected = selectedSubConversationId === subConv._id;
    const lastUpdate = subConv?.lastMessage?.updatedAt || subConv?.updatedAt;

    return (
      <Box
        key={subConv._id}
        onClick={() => handleSubConversationSelect(subConv._id)}
        sx={{
          ...(isCollapsed
            ? {
                position: "absolute",
                top: { xs: stackIndex * 10, sm: stackIndex * 15 },
                left: stackIndex * 8,
                right: stackIndex * 8,
                zIndex: isSelected ? 10 : 3 - stackIndex,
              }
            : {
                position: "relative",
              }),
          backgroundColor: "#ffffff",
          border: isSelected
            ? "2px solid #7C3AED"
            : "1px solid #e2e8f0",
          borderRadius: { xs: 1.5, sm: 2 },
          padding: { xs: 1, sm: 1.5 },
          boxShadow: isSelected
            ? "0 4px 16px rgba(124, 58, 237, 0.25)"
            : "0 1px 4px rgba(0, 0, 0, 0.08)",
          transition: "all 0.2s ease",
          cursor: "pointer",
          "&:hover": {
            transform: isSelected ? "translateY(-1px)" : "translateY(-1px)",
            boxShadow: isSelected
              ? "0 6px 20px rgba(124, 58, 237, 0.3)"
              : "0 3px 10px rgba(0, 0, 0, 0.12)",
            zIndex: isCollapsed ? 10 : undefined,
          },
        }}
      >
        {/* Row 1: Time + Menu */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: { xs: 12, sm: 16 }, color: "#6b7280" }} />
            <Typography
              variant="caption"
              sx={{
                color: "#6b7280",
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                fontWeight: 500,
              }}
            >
              {lastUpdate ? moment(lastUpdate).fromNow() : moment(subConv?.createdAt).fromNow()}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onSubConversationMenuOpen?.(event, subConv);
            }}
            sx={{
              padding: { xs: 0.25, sm: 0.5 },
              color: "#6b7280",
              "&:hover": {
                color: "#7C3AED",
                backgroundColor: "rgba(124, 58, 237, 0.1)",
              },
            }}
          >
            <MoreVertIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
          </IconButton>
        </Box>

        {/* Row 2: Title + Status */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.25,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#000000",
              fontSize: { xs: "0.8rem", sm: "0.95rem" },
              fontWeight: 700,
              textTransform: "lowercase",
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            {subConv.categoryId?.name || subConv.title || "no title"}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: subConv.status === "closed" ? "#DC2626" : "#7C3AED",
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              fontWeight: 600,
              textTransform: "lowercase",
              flexShrink: 0,
            }}
          >
            {subConv.status || "pending"}
          </Typography>
        </Box>

        {/* Row 3: Last message (hidden on xs in collapsed mode) */}
        {subConv?.lastMessage?.message && (
          <Typography
            variant="body2"
            sx={{
              color: "#6b7280",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
              mb: 0.25,
              display: isCollapsed ? { xs: "none", sm: "block" } : "block",
            }}
          >
            {subConv.lastMessage.message}
          </Typography>
        )}

        {/* Row 4: Ticket + Date */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#9ca3af",
              fontSize: { xs: "0.6rem", sm: "0.7rem" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: { xs: "45%", sm: "60%" },
            }}
          >
            #{subConv.ticketNo || subConv._id?.slice(-8)}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: "#9ca3af",
              fontSize: { xs: "0.6rem", sm: "0.7rem" },
              flexShrink: 0,
            }}
          >
            {moment(lastUpdate || subConv?.createdAt).format("DD/MM/YY HH:mm")}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f8fafc",
        padding: { xs: 1, sm: 2, md: 3 },
        marginBottom: 0.5,
      }}
    >
      {/* Subconversations Cards Container */}
      <Box sx={{ position: "relative" }}>
        {/* Scroll Indicators */}
        {isExpanded && subConversations.length > 5 && (
          <>
            {/* Top indicator - shows when there's content above */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "16px",
                background: "linear-gradient(#f8fafc, transparent)",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
            {/* Bottom indicator - shows when there's content below */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "16px",
                background: "linear-gradient(transparent, #f8fafc)",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
          </>
        )}

        {/* Expand/Collapse Button - Positioned at bottom left like download arrow */}
        <Box
          sx={{
            position: "absolute",
            bottom: { xs: "-24px", sm: "-32px" },
            left: { xs: "4px", sm: "10px" },
            zIndex: 2,
          }}
        >
          <IconButton
            size="small"
            onClick={handleExpandToggle}
            sx={{
              backgroundColor: "#6b7280",
              color: "#ffffff",
              padding: { xs: 0.5, sm: 0.75 },
              borderRadius: 1,
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
              "&:hover": {
                backgroundColor: "#7C3AED",
                boxShadow: "0 3px 10px rgba(124, 58, 237, 0.3)",
              },
            }}
          >
            {isExpanded ? (
              <ExpandLessIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />
            )}
          </IconButton>
        </Box>

        {/* Collapsed View - Overlapping Cards (First 3) */}
        {!isExpanded && (
          <Box sx={{ position: "relative", height: { xs: 80, sm: 100, md: 120 } }}>
            {getSortedSubConversations()
              .slice(0, 3)
              .map((subConv, index) => renderCard(subConv, { isCollapsed: true, stackIndex: index }))}
          </Box>
        )}

        {/* Expanded View - All Cards */}
        {isExpanded && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxHeight: { xs: "250px", sm: "320px", md: "400px" },
              overflowY: "auto",
              overflowX: "hidden",
              pr: "6px",
              scrollBehavior: "smooth",
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "2px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c1c1",
                borderRadius: "2px",
                "&:hover": { backgroundColor: "#a8a8a8" },
              },
              // Firefox scrollbar styling
              scrollbarWidth: "thin",
              scrollbarColor: "#c1c1c1 #f1f1f1",
            }}
          >
            {getSortedSubConversations().map((subConv) => renderCard(subConv))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SubConversations;
